import { BottomTabBar } from "@react-navigation/bottom-tabs";
import type { BottomTabBarProps } from "@react-navigation/bottom-tabs";
import { useTheme } from "@/hooks/useTheme";
import { Platform, View, StyleSheet } from "react-native";
import { Button } from "@/components/ui/Button";
import Animated, { useSharedValue, useAnimatedStyle, withTiming } from "react-native-reanimated";
import { useContext, useState, useRef } from "react";
import { useStorage } from "@/hooks/useStorage";
import useContextMenu from "@/hooks/useContextMenu";
import { RootLayoutContext } from "@/app/(app)/_layout";

type choice = "expanded" | "collapsed" | "auto";

export default function CustomTabBarWrapper(props: BottomTabBarProps) {
  const TAB_STORAGE_KEY = "@app_menu_width";
  const storage = useStorage();
  const { colors } = useTheme();
  const contextMenu = useContextMenu();

  const expandedWidth = 300;
  const collapsedWidth = 67;
  const buttonRef = useRef<View>(null);
  const [buttonHover, setButtonHover] = useState(false);

  const rootLayoutCtx = useContext(RootLayoutContext);

  const savedChoice = () => {
    const s = storage.getItem(TAB_STORAGE_KEY);
    if (s === "auto") return "auto";
    if (s === "collapsed") return "collapsed";
    return "expanded";
  };

  const [tabPosition, setTabPosition] = useState<choice>(savedChoice());
  const width = useSharedValue(tabPosition === "expanded" ? expandedWidth : collapsedWidth);

  const handleMenuChoice = (choice: choice) => {
    setTabPosition(choice);
    rootLayoutCtx?.setTabsCollapsed(choice === "collapsed");
    storage.saveFast(TAB_STORAGE_KEY, choice);
    if (choice === "expanded") {
      width.set(expandedWidth);
    } else if (choice === "collapsed") {
      width.set(collapsedWidth);
    }
    contextMenu.setVisible(false);
  };

  const animatedStyle = useAnimatedStyle(() => ({
    width: withTiming(width.get(), { duration: 200 }),
  }));

  const handleHoverIn = () => {
    if (tabPosition === "auto") {
      width.set(withTiming(expandedWidth, { duration: 200 }));
    }
  };

  const handleHoverOut = () => {
    if (tabPosition === "auto") {
      width.set(withTiming(collapsedWidth, { duration: 200 }));
    }
  };

  const menuContent = (
    <View style={[styles.menu, { backgroundColor: colors.card }]}>
      <Button
        text="Auto"
        style={[styles.menuButton, savedChoice() === "auto" && { backgroundColor: colors.primary }]}
        onPress={() => handleMenuChoice("auto")}
      />
      <Button
        text="Collapsed"
        style={[styles.menuButton, savedChoice() === "collapsed" && { backgroundColor: colors.primary }]}
        onPress={() => handleMenuChoice("collapsed")}
      />
      <Button
        text="Expanded"
        style={[styles.menuButton, savedChoice() === "expanded" && { backgroundColor: colors.primary }]}
        onPress={() => handleMenuChoice("expanded")}
      />
    </View>
  );

  // IOS & Android early return
  if (Platform.OS !== "web") return <BottomTabBar {...props} />;
  return (
    <Animated.View
      onPointerEnter={handleHoverIn}
      onPointerLeave={handleHoverOut}
      style={[
        animatedStyle,
        styles.container,
        {
          backgroundColor: colors.backgroundSecondary,
          borderColor: colors.border,
        },
      ]}
    >
      <BottomTabBar {...props} />
      <Button
        ref={buttonRef}
        icon={"pin"}
        onHoverIn={() => setButtonHover(true)}
        onHoverOut={() => setButtonHover(false)}
        style={[
          styles.button,
          // contextMenu.visible && { backgroundColor: colors.primary },
          buttonHover && { backgroundColor: colors.border },
        ]}
        onPress={() => {
          // Measure fresh on press to get accurate position
          buttonRef.current?.measureInWindow((x, y) => {
            contextMenu.setLocation({ x: x + 40, y: y - 80 } as any);
            contextMenu.setMenuContent(menuContent);
            contextMenu.setVisible(true);
          });
        }}
      />
    </Animated.View>
  );
}

const styles = StyleSheet.create({
  container: {
    left: 0,
    top: 0,
    bottom: 0,
    borderRightWidth: 1,
  },
  button: {
    marginTop: "auto",
    marginHorizontal: "auto",
    backgroundColor: "transparent",
    bottom: 14,
    left: 15,
    width: 36,
    height: 36,
    borderRadius: 6,
    position: "fixed",
  },
  contextMenu: {
    position: "fixed",
    bottom: 52,
    left: 48,
  },
  menu: {
    flex: 1,
    flexDirection: "column",
  },
  bullet: {
    width: 20,
  },
  menuButton: {
    paddingHorizontal: 4,
    flex: 1,
    flexDirection: "row",
    backgroundColor: "transparent",
    borderColor: "transparent",
  },
});
